import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api, fetchBlobUrl, uploadFile,
  type ClientContact, type ClientDetail, type ClientDoc, type ClientFields, type DocTip, type Extracted, type UploadResult,
} from '../../api';
import { ErrorMsg, Modal } from '../../components/Modal';
import { addressLine, checkCnp, expiryStatus, fmtDate, prepareUpload, TIP_LABEL } from '../../lib/clients';
import { countryOptions, joinPhone, splitPhone } from '../../lib/dialCodes';
import { findLocalitate, judetCode, useGeo, type Geo } from '../../lib/geo';

const EMPTY: ClientFields = {
  nume: '', prenume: null, numeAnterior: null, telefon: null, email: null, strada: null, numar: null, bloc: null, scara: null,
  etaj: null, apartament: null, localitate: null, judet: null, codPostal: null, alteDetaliiAdresa: null,
  dataNasterii: null, birthdayReminder: false, observatii: null,
};

/** Fișa clientului: nou (/a/:appId/nou) sau existent (/clienti/:id). */
export function ClientPage() {
  const params = useParams();
  const navigate = useNavigate();
  const geo = useGeo();
  const clientId = params.id ? Number(params.id) : null;
  const [appId, setAppId] = useState<number | null>(params.appId ? Number(params.appId) : null);

  const [form, setForm] = useState<ClientFields>(EMPTY);
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [docs, setDocs] = useState<ClientDoc[]>([]);
  const [can, setCan] = useState({ edit: true, delete: false, sensitive: true });
  const [cnp, setCnp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!clientId);
  const [pendingDocs, setPendingDocs] = useState<number[]>([]);
  const [lastExtract, setLastExtract] = useState<Extracted | null>(null);
  const [editingDoc, setEditingDoc] = useState<ClientDoc | null>(null);

  async function load() {
    if (!clientId) return;
    try {
      const r = await api.get<{ client: ClientDetail; documents: ClientDoc[]; contacts: ClientContact[]; can: typeof can }>(`/clients/${clientId}`);
      setDetail(r.client);
      setAppId(r.client.appId);
      const f = { ...EMPTY };
      for (const k of Object.keys(EMPTY) as (keyof ClientFields)[]) (f as Record<string, unknown>)[k] = r.client[k];
      setForm(f);
      setContacts(r.contacts);
      setDocs(r.documents);
      setCan(r.can);
      setCnp(r.client.cnp ?? '');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof ClientFields>(k: K, v: ClientFields[K]) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };
  const readOnly = !!clientId && !can.edit;

  /** CNP valid → data nașterii se completează automat */
  function changeCnp(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 13);
    setCnp(digits);
    setSaved(false);
    const c = checkCnp(digits);
    if (c.valid && c.birthDate) setForm((f) => ({ ...f, dataNasterii: c.birthDate! }));
  }

  /** Completează fișa din datele citite din act (fără să suprascrie ce e deja completat, în afară de cazul cerut). */
  function applyExtract(ex: Extracted, overwrite: boolean) {
    setForm((f) => {
      const n = { ...f };
      const put = (k: keyof ClientFields, v: string | undefined) => {
        if (v && (overwrite || !n[k])) (n as Record<string, unknown>)[k] = v;
      };
      put('nume', ex.nume); put('prenume', ex.prenume); put('dataNasterii', ex.dataNasterii);
      const a = ex.adresa;
      if (a) {
        put('strada', a.strada); put('numar', a.numar); put('bloc', a.bloc); put('scara', a.scara); put('etaj', a.etaj);
        put('apartament', a.apartament); put('judet', a.judet);
        // denumirea oficială a localității + codul poștal din nomenclator
        const found = geo ? findLocalitate(geo, a.judet ?? n.judet, a.localitate) : undefined;
        put('localitate', found?.[0] ?? a.localitate);
        put('codPostal', a.codPostal ?? (found?.[1] || undefined));
      }
      return n;
    });
    if (ex.cnp && (overwrite || !cnp)) setCnp(ex.cnp);
    setSaved(false);
  }

  async function onUpload(file: File, tip?: DocTip) {
    setError(null);
    const { blob, name } = await prepareUpload(file);
    if (blob.size > 10 * 1024 * 1024) throw new Error('Fișierul are peste 10 MB.');
    const url = clientId ? `/clients/${clientId}/documents` : `/apps/${appId}/documents`;
    const r = await uploadFile<UploadResult>(url, blob, name, tip ? `&tip=${tip}` : '');
    setDocs((d) => [r.document, ...d]);
    if (!clientId) setPendingDocs((p) => [...p, Number(r.document.id)]);
    if (r.extracted) {
      setLastExtract(r.extracted);
      if (!clientId) applyExtract(r.extracted, false);
    } else if (r.extractError) {
      setError(`Documentul a fost salvat, dar datele nu au putut fi citite automat: ${r.extractError}`);
    }
    return r;
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...form,
        cnp: can.sensitive ? cnp : undefined,
        documentIds: pendingDocs,
        contacts: contacts.filter((c) => c.valoare.trim()),
      };
      if (clientId) {
        await api.put(`/clients/${clientId}`, body);
        setPendingDocs([]);
        await load();
        setSaved(true);
      } else {
        const r = await api.post<{ id: number }>(`/apps/${appId}/clients`, body);
        navigate(`/clienti/${r.id}`, { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const cnpCheck = cnp ? checkCnp(cnp) : null;
  const title = clientId ? [form.nume, form.prenume].filter(Boolean).join(' ') || 'Client' : 'Client nou';

  if (loading) return <p className="muted">Se încarcă…</p>;

  return (
    <div className="client-page">
      <div className="page-head">
        <div>
          {appId && <button className="link small" onClick={() => navigate(`/a/${appId}`)}>← Clienți</button>}
          <h1>{title} {clientId && <span className="client-id">ID {String(clientId).padStart(6, '0')}</span>}</h1>
        </div>
        <div className="actions-inline">
          {clientId && can.delete && (
            <button className="link danger" onClick={async () => {
              if (!window.confirm(`Arhivați clientul ${title}?`)) return;
              await api.del(`/clients/${clientId}`);
              navigate(`/a/${appId}`);
            }}>Arhivează</button>
          )}
          {!readOnly && <button className="primary" disabled={busy || !form.nume.trim()} onClick={save}>{busy ? 'Se salvează…' : clientId ? 'Salvează' : 'Creează clientul'}</button>}
        </div>
      </div>
      <ErrorMsg error={error} />
      {saved && <p className="tag ok">Modificările au fost salvate.</p>}

      {!clientId && (
        <UploadZone onFile={(f) => onUpload(f)} big
          title="Încărcați actul de identitate"
          hint="Carte de identitate, pașaport sau permis de conducere (poză sau PDF). Datele se completează automat — verificați-le înainte de salvare." />
      )}

      {lastExtract && (
        <ExtractNotice ex={lastExtract} existing={!!clientId} onApply={() => { applyExtract(lastExtract, true); setLastExtract(null); }}
          onClose={() => setLastExtract(null)} />
      )}

      <Section title="Informații personale">
        <div className="grid g3">
          <Field label="Nume *"><input value={form.nume} onChange={(e) => set('nume', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Prenume"><input value={form.prenume ?? ''} onChange={(e) => set('prenume', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Nume / Denumire anterioară"><input value={form.numeAnterior ?? ''} onChange={(e) => set('numeAnterior', e.target.value)} disabled={readOnly} /></Field>
        </div>
        <div className="grid g2">
          <Field label="Telefon"><PhoneInput value={form.telefon} onChange={(v) => set('telefon', v)} disabled={readOnly} /></Field>
          <Field label="Email"><input type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} disabled={readOnly} /></Field>
        </div>
        <ExtraContacts contacts={contacts} readOnly={readOnly} onChange={(c) => { setContacts(c); setSaved(false); }} />

        <hr />
        <div className="subhead"><strong>ADRESĂ</strong> <span className="muted">preluată automat din actul de identitate sau completată manual</span></div>
        <div className="grid addr">
          <Field label="Stradă"><input value={form.strada ?? ''} onChange={(e) => set('strada', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Nr."><input value={form.numar ?? ''} onChange={(e) => set('numar', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Bloc"><input value={form.bloc ?? ''} onChange={(e) => set('bloc', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Sc."><input value={form.scara ?? ''} onChange={(e) => set('scara', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Et."><input value={form.etaj ?? ''} onChange={(e) => set('etaj', e.target.value)} disabled={readOnly} /></Field>
          <Field label="Ap."><input value={form.apartament ?? ''} onChange={(e) => set('apartament', e.target.value)} disabled={readOnly} /></Field>
        </div>
        <LocalitateJudet geo={geo} form={form} readOnly={readOnly}
          onChange={(patch) => { setForm((f) => ({ ...f, ...patch })); setSaved(false); }} />
        <Field label="Alte detalii adresă">
          <input value={form.alteDetaliiAdresa ?? ''} onChange={(e) => set('alteDetaliiAdresa', e.target.value)} disabled={readOnly}
            placeholder="ex. interfon 12, casa din spate, reper…" />
        </Field>
        {addressLine(form) && <p className="addr-line"><strong>În documente:</strong> {addressLine(form)}</p>}

        <hr />
        <div className="grid g2">
          <Field label="CNP (opțional)">
            {can.sensitive ? (
              <div className="cnp-row">
                <input className="mono" maxLength={13} inputMode="numeric" value={cnp} disabled={readOnly}
                  onChange={(e) => changeCnp(e.target.value)} placeholder="13 cifre" />
                {cnp && !readOnly && (
                  <button type="button" className="link icon" title="Șterge CNP-ul" onClick={() => changeCnp('')}>🗑</button>
                )}
              </div>
            ) : (
              <input disabled value={detail?.hasCnp ? 'completat (nu aveți drept de vizualizare)' : ''} />
            )}
            {cnpCheck?.valid && <span className="ok-text small">✓ CNP valid (cifra de control corectă) · dată naștere {fmtDate(cnpCheck.birthDate)}</span>}
            {cnp && cnpCheck && !cnpCheck.valid && <span className="error-text small">✗ {cnp.length < 13 ? `CNP incomplet (${cnp.length}/13 cifre)` : cnpCheck.reason}</span>}
          </Field>
          <Field label="Data nașterii (opțional)">
            <div className="cnp-row">
              <input type="date" value={form.dataNasterii ?? ''} onChange={(e) => set('dataNasterii', e.target.value || null)} disabled={readOnly} />
              {form.dataNasterii && !readOnly && <button type="button" className="link icon" title="Șterge data" onClick={() => set('dataNasterii', null)}>🗑</button>}
            </div>
            {cnpCheck?.valid && form.dataNasterii && cnpCheck.birthDate !== form.dataNasterii && (
              <span className="warn-text small">Data diferă de cea din CNP ({fmtDate(cnpCheck.birthDate)}).</span>
            )}
            <label className="checkbox bday">
              <input type="checkbox" checked={form.birthdayReminder} onChange={(e) => set('birthdayReminder', e.target.checked)} disabled={readOnly || !form.dataNasterii} />
              <span>🎂 Anunță-mă când este ziua clientului<br /><span className="muted small">Primești email în dimineața zilei de naștere.</span></span>
            </label>
          </Field>
        </div>
      </Section>

      <Section title="Documente de identitate" right={clientId && can.edit ? <AddDocButton onFile={(f, t) => onUpload(f, t)} /> : undefined}>
        {docs.length === 0 && <p className="muted">Niciun document încă.{clientId ? ' Folosiți „+ Adaugă document”.' : ''}</p>}
        {docs.length > 0 && (
          <table className="docs">
            <thead><tr><th>Tip</th><th>Serie / număr</th><th>Emis de</th><th>Emitere</th><th>Expirare</th><th /></tr></thead>
            <tbody>
              {docs.map((d) => {
                const st = expiryStatus(d.dataExpirarii);
                return (
                  <tr key={d.id}>
                    <td>{TIP_LABEL[d.tipDocument]}</td>
                    <td className="mono">{[d.serie, d.numar].filter(Boolean).join(' ') || '—'}</td>
                    <td>{d.emisDe ?? '—'}</td>
                    <td>{fmtDate(d.dataEmiterii)}</td>
                    <td>{fmtDate(d.dataExpirarii)} {st && <span className={st.cls}>{st.label}</span>}</td>
                    <td className="right nowrap">
                      {can.sensitive && <button className="link" onClick={async () => {
                        const w = window.open('', '_blank');
                        const u = await fetchBlobUrl(`/documents/${d.id}/file`);
                        if (w && u) w.location.href = u; else if (w) w.close();
                      }}>Vezi</button>}
                      {(can.edit || !clientId) && <button className="link" onClick={() => setEditingDoc(d)}>Editează</button>}
                      {(can.edit || !clientId) && <button className="link danger" onClick={async () => {
                        if (!window.confirm('Ștergeți documentul?')) return;
                        await api.del(`/documents/${d.id}`);
                        setDocs((x) => x.filter((y) => y.id !== d.id));
                        setPendingDocs((p) => p.filter((y) => y !== d.id));
                      }}>Șterge</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!clientId && docs.length > 0 && <p className="muted small">Documentele se atașează clientului la salvare.</p>}
      </Section>

      <Section title="Observații" collapsed={!form.observatii}>
        <textarea rows={4} value={form.observatii ?? ''} onChange={(e) => set('observatii', e.target.value)} disabled={readOnly} />
      </Section>

      {editingDoc && (
        <DocModal doc={editingDoc} onClose={() => setEditingDoc(null)}
          onSaved={(d) => { setDocs((x) => x.map((y) => (y.id === d.id ? d : y))); setEditingDoc(null); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
function Section({ title, children, right, collapsed }: { title: string; children: ReactNode; right?: ReactNode; collapsed?: boolean }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <section className="card-section">
      <header onClick={() => setOpen(!open)}>
        <h2>{title}</h2>
        <span className="spacer" />
        <span onClick={(e) => e.stopPropagation()}>{right}</span>
        <span className="chev">{open ? '︿' : '﹀'}</span>
      </header>
      {open && <div className="body">{children}</div>}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span className="flabel">{label}</span>{children}</label>;
}

/** Telefon cu prefixul țării (listă cu steaguri). Valoarea se păstrează în format internațional: +40783069625. */
export function PhoneInput({ value, onChange, disabled }: { value: string | null; onChange: (v: string | null) => void; disabled?: boolean }) {
  const initial = splitPhone(value);
  const [iso, setIso] = useState(initial.iso);
  const [num, setNum] = useState(initial.number);
  const options = useMemo(countryOptions, []);
  useEffect(() => {
    const s = splitPhone(value);
    if (joinPhone(iso, num) !== (value || null)) { setIso(s.iso); setNum(s.number); }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const code = options.find((o) => o.iso === iso)?.code ?? '';
  return (
    <div className="phone-input">
      <select value={iso} disabled={disabled} onChange={(e) => { setIso(e.target.value); onChange(joinPhone(e.target.value, num)); }}
        title="Prefixul țării">
        {options.map((o) => <option key={o.iso} value={o.iso}>{o.label}</option>)}
      </select>
      <span className="phone-code">{options.find((o) => o.iso === iso)?.label.split(' ')[0]} {code}</span>
      <input type="tel" inputMode="tel" value={num} disabled={disabled} placeholder="7xx xxx xxx"
        onChange={(e) => { const v = e.target.value.replace(/[^0-9 ]/g, ''); setNum(v); onChange(joinPhone(iso, v)); }} />
    </div>
  );
}

/** Telefoane și e-mailuri suplimentare. */
function ExtraContacts({ contacts, onChange, readOnly }: { contacts: ClientContact[]; onChange: (c: ClientContact[]) => void; readOnly: boolean }) {
  const update = (i: number, patch: Partial<ClientContact>) => onChange(contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <div className="extra-contacts">
      {contacts.map((c, i) => (
        <div key={i} className="extra-row">
          <span className="extra-kind">{c.tip === 'Telefon' ? '📞' : '✉️'}</span>
          {c.tip === 'Telefon'
            ? <PhoneInput value={c.valoare} disabled={readOnly} onChange={(v) => update(i, { valoare: v ?? '' })} />
            : <input type="email" value={c.valoare} disabled={readOnly} onChange={(e) => update(i, { valoare: e.target.value })} placeholder="adresa@exemplu.ro" />}
          <input className="extra-label" value={c.eticheta ?? ''} disabled={readOnly} placeholder="etichetă (ex. serviciu)"
            onChange={(e) => update(i, { eticheta: e.target.value || null })} />
          {!readOnly && <button type="button" className="link icon" title="Elimină" onClick={() => onChange(contacts.filter((_, j) => j !== i))}>🗑</button>}
        </div>
      ))}
      {!readOnly && (
        <div className="extra-add">
          <button type="button" className="link" onClick={() => onChange([...contacts, { tip: 'Telefon', valoare: '', eticheta: null }])}>+ Adaugă telefon</button>
          <button type="button" className="link" onClick={() => onChange([...contacts, { tip: 'Email', valoare: '', eticheta: null }])}>+ Adaugă e-mail</button>
        </div>
      )}
    </div>
  );
}

/** Localitate + județ din nomenclatorul României; codul poștal se completează automat când există unul pentru localitate. */
function LocalitateJudet({ geo, form, onChange, readOnly }: {
  geo: Geo | null; form: ClientFields; onChange: (p: Partial<ClientFields>) => void; readOnly: boolean;
}) {
  const code = geo ? judetCode(geo, form.judet) : undefined;
  const list = geo && code ? geo.localitati[code] ?? [] : [];
  const listId = `loc-${code ?? 'none'}`;
  const judetKnown = !form.judet || !!code;
  return (
    <div className="grid g3w">
      <Field label="Localitate">
        <input list={listId} value={form.localitate ?? ''} disabled={readOnly} autoComplete="off"
          placeholder={form.judet ? 'Căutați localitatea…' : 'Alegeți întâi județul'}
          onChange={(e) => {
            const v = e.target.value;
            const hit = list.find(([n]) => n === v);
            onChange({ localitate: v, ...(hit?.[1] ? { codPostal: hit[1] } : {}) });
          }} />
        <datalist id={listId}>{list.map(([n]) => <option key={n} value={n} />)}</datalist>
      </Field>
      <Field label="Județ">
        <select value={judetKnown ? (geo?.judete.find(([c]) => c === code)?.[1] ?? form.judet ?? '') : form.judet ?? ''} disabled={readOnly || !geo}
          onChange={(e) => onChange({ judet: e.target.value || null, localitate: null, codPostal: null })}>
          <option value="">{geo ? '— alegeți —' : 'Se încarcă…'}</option>
          {!judetKnown && <option value={form.judet!}>{form.judet}</option>}
          {geo?.judete.map(([c, n]) => <option key={c} value={n}>{n}</option>)}
        </select>
      </Field>
      <Field label="Cod poștal">
        <input value={form.codPostal ?? ''} onChange={(e) => onChange({ codPostal: e.target.value.replace(/\D/g, '') || null })}
          disabled={readOnly} inputMode="numeric" maxLength={6} />
      </Field>
    </div>
  );
}

function UploadZone({ onFile, title, hint, big }: { onFile: (f: File) => Promise<unknown>; title: string; hint: string; big?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  async function handle(f: File | undefined) {
    if (!f) return;
    setBusy(true);
    setError(null);
    try { await onFile(f); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className={`upload-zone ${big ? 'big' : ''} ${over ? 'over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files[0]); }}
      onClick={() => !busy && input.current?.click()}>
      <input ref={input} type="file" accept="image/*,application/pdf" hidden
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="uz-icon">🪪</div>
      <div>
        <strong>{busy ? 'Se citește documentul…' : title}</strong>
        <div className="muted small">{busy ? 'Durează câteva secunde.' : hint}</div>
        {error && <div className="error-text small">{error}</div>}
      </div>
    </div>
  );
}

function AddDocButton({ onFile }: { onFile: (f: File, tip?: DocTip) => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="primary small" onClick={() => setOpen(true)}>+ Adaugă document</button>
      {open && (
        <Modal title="Adaugă document" onClose={() => setOpen(false)}>
          <UploadZone onFile={async (f) => { await onFile(f); setOpen(false); }} title="Alegeți poza sau PDF-ul actului"
            hint="Tipul, seria, numărul și datele de emitere/expirare se citesc automat." />
        </Modal>
      )}
    </>
  );
}

function ExtractNotice({ ex, existing, onApply, onClose }: { ex: Extracted; existing: boolean; onApply: () => void; onClose: () => void }) {
  const rows: [string, string | undefined][] = [
    ['Tip', TIP_LABEL[ex.tipDocument]], ['Nume', [ex.nume, ex.prenume].filter(Boolean).join(' ')], ['CNP', ex.cnp],
    ['Serie / nr.', [ex.serie, ex.numar].filter(Boolean).join(' ')], ['Emis', ex.dataEmiterii && fmtDate(ex.dataEmiterii)],
    ['Expiră', ex.dataExpirarii && fmtDate(ex.dataExpirarii)],
  ];
  return (
    <div className="extract-notice">
      <div className="spread">
        <strong>Date citite din document</strong>
        <button className="link" onClick={onClose}>✕</button>
      </div>
      <div className="kv">{rows.filter((r) => r[1]).map(([k, v]) => <span key={k}><span className="muted">{k}:</span> {v}</span>)}</div>
      {ex.avertismente.map((w) => <p key={w} className="warn-text small">⚠ {w}</p>)}
      {existing
        ? <button className="link" onClick={onApply}>Actualizează fișa cu datele din document</button>
        : <p className="muted small">Câmpurile de mai jos au fost completate. Verificați-le înainte de salvare.</p>}
    </div>
  );
}

function DocModal({ doc, onClose, onSaved }: { doc: ClientDoc; onClose: () => void; onSaved: (d: ClientDoc) => void }) {
  const [d, setD] = useState(doc);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    try {
      await api.put(`/documents/${d.id}`, {
        tipDocument: d.tipDocument, serie: d.serie, numar: d.numar, emisDe: d.emisDe, dataEmiterii: d.dataEmiterii, dataExpirarii: d.dataExpirarii,
      });
      onSaved(d);
    } catch (e) { setError((e as Error).message); }
  }
  return (
    <Modal title="Document" onClose={onClose}>
      <label>Tip
        <select value={d.tipDocument} onChange={(e) => setD({ ...d, tipDocument: e.target.value as DocTip })}>
          {(Object.keys(TIP_LABEL) as DocTip[]).map((t) => <option key={t} value={t}>{TIP_LABEL[t]}</option>)}
        </select>
      </label>
      <div className="grid g2">
        <label>Serie<input value={d.serie ?? ''} onChange={(e) => setD({ ...d, serie: e.target.value || null })} /></label>
        <label>Număr<input value={d.numar ?? ''} onChange={(e) => setD({ ...d, numar: e.target.value || null })} /></label>
      </div>
      <label>Emis de<input value={d.emisDe ?? ''} onChange={(e) => setD({ ...d, emisDe: e.target.value || null })} /></label>
      <div className="grid g2">
        <label>Data emiterii<input type="date" value={d.dataEmiterii ?? ''} onChange={(e) => setD({ ...d, dataEmiterii: e.target.value || null })} /></label>
        <label>Data expirării<input type="date" value={d.dataExpirarii ?? ''} onChange={(e) => setD({ ...d, dataExpirarii: e.target.value || null })} /></label>
      </div>
      <ErrorMsg error={error} />
      <div className="actions"><button onClick={onClose}>Renunță</button><button className="primary" onClick={save}>Salvează</button></div>
    </Modal>
  );
}
